import { Box, Container, Spinner, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useMemo } from 'react';

interface PluginPageProps {
  pluginId: string;
}

const decodeBase64Utf8 = (base64String?: string): string => {
  if (!base64String) return '';

  try {
    const binaryString = atob(base64String);
    const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes).replaceAll('text-align: center', 'justify-self: center;');
  } catch (error) {
    console.error('Failed to decode base64 description:', error);
    return '';
  }
};

const fetchPlugin = async (pluginId: string) => {
  const { data } = await axios.get(`https://api.spiget.org/v2/resources/${pluginId}`);
  return data;
};

export default function PluginPage({ pluginId }: PluginPageProps) {
  const { data: plugin, isLoading, isError } = useQuery({
    queryKey: ['plugin', pluginId],
    queryFn: () => fetchPlugin(pluginId),
    enabled: Boolean(pluginId),
  });

  const pluginDescriptionHtml = useMemo(
    () => decodeBase64Utf8(plugin?.description),
    [plugin?.description]
  );

  if (isLoading) {
    return (
      <Container centerContent py={10}>
        <Spinner />
      </Container>
    );
  }

  if (isError || !plugin) {
    return (
      <Container centerContent py={10}>
        <Text color="red.500">Error loading plugin details.</Text>
      </Container>
    );
  }

  return (
    <Container p={0} maxW="full">
      <Box
        flex="1"
        p={8}
        bg="gray.50"
        color="gray.600"
        borderWidth="1px"
        borderColor="gray.300"
        borderRadius={{ base: 0, md: 4 }}
      >
        <div dangerouslySetInnerHTML={{ __html: pluginDescriptionHtml }} />
      </Box>
    </Container>
  );
}